import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import menuCategories from "../data/menuCategories.json";

/* ─── Bowl / Flame Icon SVG ────────────────────────────────────── */
const BowlIcon = () => (
  <svg
    viewBox="0 0 64 64"
    className="w-10 h-10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Steam lines */}
    <path
      d="M22 10 C22 6 26 6 26 10 C26 14 22 14 22 18"
      stroke="#e02237"
      strokeWidth="2.5"
      strokeLinecap="round"
      fill="none"
    />
    <path
      d="M31 8 C31 4 35 4 35 8 C35 12 31 12 31 16"
      stroke="#e02237"
      strokeWidth="2.5"
      strokeLinecap="round"
      fill="none"
    />
    <path
      d="M40 10 C40 6 44 6 44 10 C44 14 40 14 40 18"
      stroke="#e02237"
      strokeWidth="2.5"
      strokeLinecap="round"
      fill="none"
    />
    {/* Bowl body */}
    <path d="M10 28 C10 44 20 52 32 52 C44 52 54 44 54 28 Z" fill="#e02237" />
    {/* Bowl rim */}
    <rect x="9" y="25" width="46" height="5" rx="2.5" fill="#c0192a" />
    {/* Chopsticks */}
    <line
      x1="20"
      y1="22"
      x2="36"
      y2="46"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <line
      x1="28"
      y1="20"
      x2="44"
      y2="44"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
    />
    {/* Noodle swirl */}
    <ellipse cx="32" cy="33" rx="10" ry="5" fill="#c0192a" opacity="0.6" />
  </svg>
);

/* ─── Category Card ───────────────────────────────────────────── */
const CategoryCard = ({ item }) => {
  const navigate = useNavigate();
  const handleClick = () => navigate(`/menu/${item.slug}/${item.id}`);

  return (
    <div
      className="group relative flex-shrink-0 rounded-xl overflow-hidden cursor-pointer bg-[#0a0204] border border-white/10 hover:border-[#9b1c2e]/60 transition-all duration-300 flex flex-col justify-between"
      style={{ width: "var(--card-w)", height: "420px" }}
      onClick={handleClick}
    >
      {/* Background image with hover zoom */}
      <img
        src={item.image}
        alt={item.title}
        className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
        loading="lazy"
        onError={(e) => {
          e.target.src =
            "https://images.unsplash.com/photo-1544025162-d76694265947?q=80&w=600&auto=format&fit=crop";
        }}
      />

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0e0003] via-[#0e0003]/60 to-black/30 group-hover:from-[#0e0003] group-hover:via-[#0e0003]/70 transition-all duration-300" />

      {/* Top Bar Badge & Icon */}
      <div className="relative z-10 p-4 flex items-center justify-between">
        <span className="px-3 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase bg-[#9b1c2e]/90 text-white backdrop-blur-sm shadow-md">
          {item.category}
        </span>
        <div className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm border border-white/20 flex items-center justify-center opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300">
          <BowlIcon />
        </div>
      </div>

      {/* Bottom Content Area */}
      <div className="relative z-10 p-5 pt-0 flex flex-col justify-end">
        <h3 className="text-[18px] font-bold text-white uppercase tracking-wide leading-tight mb-2 group-hover:text-[#f87171] transition-colors duration-200">
          {item.title}
        </h3>

        <p className="font-sans text-[13px] text-gray-300 leading-relaxed line-clamp-2 mb-3">
          {item.description}
        </p>

        {/* Button Row */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
          className="w-full py-2.5 rounded-lg bg-white/10 hover:bg-[#9b1c2e] border border-white/20 hover:border-[#9b1c2e] text-white text-xs font-bold uppercase tracking-widest transition-all duration-200 flex items-center justify-center gap-2 group-hover:shadow-lg"
        >
          <span>View Details</span>
          <svg
            className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M17 8l4 4m0 0l-4 4m4-4H3"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

/* ─── Background Pattern ──────────────────────────────────────── */
const BgPattern = () => (
  <svg
    className="absolute inset-0 w-full h-full opacity-[0.06] pointer-events-none select-none"
    xmlns="http://w3.org/2000/svg"
  >
    <defs>
      <pattern
        id="mcPattern"
        x="0"
        y="0"
        width="60"
        height="60"
        patternUnits="userSpaceOnUse"
      >
        <circle
          cx="30"
          cy="30"
          r="12"
          stroke="#ffffff"
          strokeWidth="1"
          fill="none"
        />
        <circle
          cx="30"
          cy="30"
          r="6"
          stroke="#ffffff"
          strokeWidth="0.5"
          fill="none"
        />
        <line
          x1="0"
          y1="30"
          x2="18"
          y2="30"
          stroke="#ffffff"
          strokeWidth="0.5"
        />
        <line
          x1="42"
          y1="30"
          x2="60"
          y2="30"
          stroke="#ffffff"
          strokeWidth="0.5"
        />
        <line
          x1="30"
          y1="0"
          x2="30"
          y2="18"
          stroke="#ffffff"
          strokeWidth="0.5"
        />
        <line
          x1="30"
          y1="42"
          x2="30"
          y2="60"
          stroke="#ffffff"
          strokeWidth="0.5"
        />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#mcPattern)" />
  </svg>
);

/* ─── Main Component ──────────────────────────────────────────── */
const MenuCategoriesSection = () => {
  const [current, setCurrent] = useState(0);
  const [visibleCount, setVisible] = useState(4);
  const [isHovered, setIsHovered] = useState(false);
  const trackRef = useRef(null);
  const timerRef = useRef(null);

  const itemsToDisplay = menuCategories;
  const total = itemsToDisplay.length;
  const maxIndex = Math.max(0, total - visibleCount);

  /* ── Responsive: recalculate visible cards ── */
  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth;
      if (w >= 1280) setVisible(4);
      else if (w >= 900) setVisible(3);
      else if (w >= 600) setVisible(2);
      else setVisible(1);
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);

  /* ── Clamp current when visible count or category items change ── */
  useEffect(() => {
    setCurrent((c) => Math.min(c, Math.max(0, total - visibleCount)));
  }, [visibleCount, total]);

  /* ── Auto-play ── */
  const stopTimer = useCallback(() => clearInterval(timerRef.current), []);
  const startTimer = useCallback(() => {
    stopTimer();
    if (maxIndex <= 0) return;
    timerRef.current = setInterval(() => {
      setCurrent((c) => (c >= maxIndex ? 0 : c + 1));
    }, 4000);
  }, [maxIndex, stopTimer]);

  useEffect(() => {
    if (!isHovered) startTimer();
    else stopTimer();
    return stopTimer;
  }, [isHovered, startTimer, stopTimer]);

  const goTo = (idx) => {
    setCurrent(Math.max(0, Math.min(idx, maxIndex)));
    startTimer();
  };

  /* ── Card width calculation ── */
  const GAP = 20; // px
  const cardWidthPct = `calc(${100 / visibleCount}% - ${(GAP * (visibleCount - 1)) / visibleCount}px)`;

  return (
    <section
      id="menu-categories"
      className="relative overflow-hidden bg-[#000000] py-16 lg:py-24 border-t border-b border-white/10"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background pattern */}
      <BgPattern />

      <div className="relative z-10 max-w-[1280px] mx-auto px-4 lg:px-8">
        {/* ── Header Row ── */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2.5 h-2.5 rounded-full bg-[#c0424e] animate-pulse" />
              <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-[#c0424e]">
                Authentic Menu Categories
              </p>
            </div>
            <h2 className="text-[32px] sm:text-[40px] lg:text-[46px] font-bold text-white uppercase leading-tight tracking-wide">
              Explore Our
              <br />
              Menu Categories
            </h2>
          </div>
        </div>

        {/* ── Carousel Track ── */}
        <div className="overflow-hidden" ref={trackRef}>
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{
              gap: `${GAP}px`,
              transform: `translateX(calc(-${current} * (${cardWidthPct} + ${GAP}px)))`,
              "--card-w": cardWidthPct,
            }}
          >
            {itemsToDisplay.map((item) => (
              <CategoryCard key={item.id} item={item} />
            ))}
          </div>
        </div>

        {/* ── Dot Indicators ── */}
        {maxIndex > 0 && (
          <div className="flex justify-center gap-2 mt-8">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`transition-all duration-300 rounded-full ${
                  i === current
                    ? "w-6 h-2 bg-[#9b1c2e]"
                    : "w-2 h-2 bg-white/25 hover:bg-white/50"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default MenuCategoriesSection;
